/* eslint-disable @typescript-eslint/no-empty-function */
import React from 'react';
import * as rxjs from 'rxjs';
import * as rxop from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import axios from 'axios';

import { getSelectedPosID } from 'reducers/PointsOfSale';
import { ErplyAttributes } from 'utils';

import {
  assertCartSatisfiesTaxRates,
  TaxRateValidatingBillRow,
  TaxRateValidationTable,
} from '../../components/TaxRateValidation';
import { getEFSTAEndpoint } from '../../API/selectors';
import { FiscalIntegration } from '../FiscalIntegration';
import { HeaderIcon } from '../../components/HeaderIcon';

import { getCurrentTransaction } from './getCurrentTransaction';
import { EFSTADETaxRates } from './constants';

const ATTRIBUTES = {
  code: 'EFSTA-DE-CODE',
  d0: 'EFSTA-DE-D0',
};

const efr = axios.create();
efr.interceptors.response.use(
  f => f,
  err => {
    try {
      const warning = err.data.TraC.Result.Warning;
      if (warning) throw new Error(warning);
    } catch (e) {
      throw err;
    }
  },
);

export const DEFiscalIntegration: FiscalIntegration = {
  getStatus: () => ({
    type: 'valid',
    message: 'Selected: DE',
  }),
  onCashInOut: sum => async (dispatch, getState) => {
    efr.defaults.baseURL = getEFSTAEndpoint(getState());
    return rxjs
      .from([
        {
          TraS: { ESR: { TL: getSelectedPosID(getState()) } },
        },
      ])
      .pipe(
        rxop.concatMap(msg => efr.post('/register', msg)),

        rxop.map(({ data: { TraSC: { Fis: { TID } } } }) => ({
          Tra: {
            ESR: {
              TL: getSelectedPosID(getState()),
              TID,
              NFS: 'PAY',
              PosA: [
                {
                  _: 'Pos',
                  Dsc: Number(sum) < 0 ? 'DKT:TenderPickup' : 'DKT:TenderLoan',
                  amt: sum,
                },
              ],
              PayA: [{ _: 'Pay', Dsc: 'Cash', PayG: '0', Amt: sum }],
            },
          },
        })),

        rxop.concatMap(msg => efr.post('/register', msg)),
      )
      .toPromise();
  },
  onCloseDay: () => async (dispatch, getState) => {
    efr.defaults.baseURL = getEFSTAEndpoint(getState());
    return efr.post('/register', {
      Tra: { ESR: { TL: getSelectedPosID(getState()), NFS: 'Z' } },
    });
  },
  onSaveDocument: requests => async (dispatch, getState) => {
    efr.defaults.baseURL = getEFSTAEndpoint(getState());
    const base = getCurrentTransaction(getState());

    const reqsWithPromises = R.map(
      R.when(R.propEq('requestName', 'saveSalesDocument'), async sdoc => {
        const attrs = ErplyAttributes.fromFlatArray(sdoc);
        const sdocNoAttrs = ErplyAttributes.withoutFlatArray(sdoc);
        const d0 = attrs.get(ATTRIBUTES.d0);
        const data = R.pipe(
          R.when(R.always(d0), R.assocPath(['TraS', 'ESR', 'D0'], d0)),
        )({
          TraS: base,
        });
        const res = await efr.post('/register', data);
        const isWipDocument = !['CASHINVOICE', 'CREDITINVOICE'].includes(
          sdoc.type,
        );
        const data2 = R.pipe(
          RA.renameKeys({
            TraS: 'Tra',
          }),
          R.assocPath(['Tra', 'ESR', 'TID'], res.data.TraSC.Fis.TID),
          R.when(
            R.always(isWipDocument),
            R.assocPath(['Tra', 'ESR', 'NFS'], 'ORDER'),
          ),
        )(data);
        const res2 = await efr.post('/register', data2);
        attrs.set(ATTRIBUTES.code, res2.data.TraC.Fis.Code);
        attrs.set(ATTRIBUTES.d0, d0 ?? res2.data.TraC.ESR.D);
        const resultingRequest = R.mergeDeepRight(
          sdocNoAttrs,
          attrs.asFlatArray,
        );
        return resultingRequest;
      }),
    )(requests) as typeof requests;
    return Promise.all(reqsWithPromises).catch(err => {
      throw new Error(
        'EFSTA DE failed to save salesDocument. Ensure the microservice is running and EFSTA is configured',
        { cause: err },
      );
    });
  },
  onOpenPaymentModal: (p, ap) => async (dispatch, getState) => {
    await dispatch(
      assertCartSatisfiesTaxRates(EFSTADETaxRates.map(r => r.group)),
    );
    return ap;
  },
  ComponentConfiguration: () => {
    return <TaxRateValidationTable efstaRates={EFSTADETaxRates} />;
  },
  ComponentHeader: () => {
    return <HeaderIcon country="DE" />;
  },
  UICustomTableBillRow: ({ order, children }) => {
    const codes = EFSTADETaxRates.map(r => r.group);
    return (
      <TaxRateValidatingBillRow order={order} allowedCodes={codes}>
        {children}
      </TaxRateValidatingBillRow>
    );
  },
};
